/**
 * The guest demo's hint catalogue (AD-844; docs/design/guest-demo.md §5.7 — the table there is
 * this file, in words). Order is priority. Each row: the trigger, what counts as acted on, and the
 * words for touch and for keyboard-and-mouse.
 *
 * Why here and not in content/: the copy is HUD chrome tied to the client's own controls (tap,
 * drag, keys), not world content; a content category for it would be a grammar change (AD-100).
 * The quest and NPC ids it names are the echo zone's (content/quests/echo-*.json).
 *
 * Copy rules: short (two lines on a phone), input-aware, never a tuning number (AD-321) — a bar
 * slot's key is a control, not a number about the game.
 */

import {
  objectiveOpen,
  objectiveStarted,
  questOf,
  type HintContext,
  type HintDef,
} from './hintEngine.js';

/** The demo's quest chain and guide (content/quests/echo-*.json, echo-warden-sel). */
export const DEMO_QUESTS = {
  gather: 'echo-first-gather',
  craft: 'echo-brew-the-draught',
  boss: 'echo-face-the-sentinel',
} as const;
export const DEMO_GUIDE_NAME = 'Warden Sel';
/** A hostile whose name contains this is the demo's boss (`echo-depths-sentinel`). */
export const DEMO_BOSS_NAME_MATCH = 'Sentinel';

const key = (ctx: HintContext, abilityId: string, fallback: string): string => {
  const slot = ctx.slotOf(abilityId);
  return slot === null ? fallback : String(slot);
};

export const DEMO_HINTS: readonly HintDef[] = [
  {
    id: 'move',
    // Acted on by walking — or by talking to the guide first (the demo spawns beside Sel, so a
    // player can take the first quest without walking; the card must not hold the next one back).
    when: (ctx) => !ctx.moved && ctx.quests.length === 0,
    done: (ctx) => ctx.moved || ctx.quests.length > 0,
    text: (ctx) =>
      ctx.input === 'touch'
        ? 'Tap the ground to walk. Drag to look around, pinch to zoom.'
        : 'WASD or click the ground to walk. Right-drag to look around, scroll to zoom.',
  },
  {
    id: 'find-guide',
    when: (ctx) => ctx.moved && !questOf(ctx, DEMO_QUESTS.gather),
    done: (ctx) => questOf(ctx, DEMO_QUESTS.gather) !== undefined,
    text: (ctx) =>
      ctx.input === 'touch'
        ? `${DEMO_GUIDE_NAME} has your first task: the gold ◆ on the Map. Walk up and tap Talk.`
        : `${DEMO_GUIDE_NAME} has your first task: the gold ◆ on the map. Walk up and press E.`,
  },
  {
    id: 'gather',
    when: (ctx) => objectiveOpen(ctx, DEMO_QUESTS.gather, 'gather'),
    done: (ctx) => objectiveStarted(ctx, DEMO_QUESTS.gather, 'gather'),
    text: (ctx) =>
      ctx.input === 'touch'
        ? 'Find the dark fenroot among the stumps and tap Gather. Your sickle comes to hand by itself.'
        : 'Find the dark fenroot among the stumps and press E. Your sickle comes to hand by itself.',
  },
  {
    id: 'fight',
    when: (ctx) => ctx.hostileNear && !ctx.dead,
    done: (ctx) => ctx.struck,
    text: (ctx) =>
      ctx.input === 'touch'
        ? 'Tap a creature to attack, hold on it for a power strike. A red ring under you? Tap Dodge.'
        : 'Click a creature to attack (F), 1–6 for abilities. A red ring under you? Space dodges.',
  },
  {
    id: 'turn-in',
    when: (ctx) => ctx.quests.some((q) => q.status === 'complete'),
    done: (ctx) => !ctx.quests.some((q) => q.status === 'complete'),
    text: (ctx) =>
      ctx.input === 'touch'
        ? `Done! Go back to ${DEMO_GUIDE_NAME} and tap Talk to hand it in.`
        : `Done! Go back to ${DEMO_GUIDE_NAME} and press E to hand it in.`,
  },
  {
    id: 'craft',
    when: (ctx) => objectiveOpen(ctx, DEMO_QUESTS.craft, 'craft'),
    done: (ctx) => !objectiveOpen(ctx, DEMO_QUESTS.craft, 'craft'),
    text: (ctx) =>
      ctx.input === 'touch'
        ? 'Follow the arrow to the Alchemy Table. Stand by it and tap Craft at Alchemy Table.'
        : 'Follow the arrow to the Alchemy Table. Stand by it and press E to craft.',
  },
  {
    id: 'heal',
    when: (ctx) => !ctx.dead && ctx.hpFrac < 0.5,
    done: (ctx) => ctx.hpFrac >= 0.8,
    text: (ctx) =>
      ctx.input === 'touch'
        ? `Hurt? Tap ${key(ctx, 'bandage', 'Bandage')} on your bar to bandage up. Your draught is in Menu → Inventory.`
        : `Hurt? Press ${key(ctx, 'bandage', 'Bandage')} to bandage up. Your draught is in the Inventory (I).`,
  },
  {
    id: 'decoy',
    when: (ctx) => ctx.bossNear && questOf(ctx, DEMO_QUESTS.boss)?.status === 'active',
    done: () => false,
    text: (ctx) =>
      ctx.input === 'touch'
        ? `When it calls its Shade, tap ${key(ctx, 'mirror-image', 'Mirror Image')} and step aside: the blows go to your image.`
        : `When it calls its Shade, press ${key(ctx, 'mirror-image', 'Mirror Image')} and step aside: the blows go to your image.`,
  },
];
